import { useRef, useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Home, BarChart3, Brain, TrendingUp, Wallet, CreditCard, Target, Building2, FileText, Users, Award, Package, UserCog, UserPlus, ChevronDown, ChevronRight, DollarSign, Briefcase, Zap, PieChart, Activity, BookOpen, FileBarChart, BarChart2, Landmark, LogOut, Database, Boxes, Shield, UserCircle, FolderOpen, FileSignature, Receipt, Calendar, Megaphone, Clock, Coins } from 'lucide-react';
import { useApp } from '../context/AppContext';

// Full navigation categories (for Director/Finance role)
const ALL_NAV_CATEGORIES = [
  {
    id: 'self-service',
    label: 'Portal Saya',
    icon: UserCircle,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    roles: ['staff'], // Staff-only sidebar
    items: [
      { id: 'me-portal', label: 'Portal Self-Service', icon: UserCircle, path: '/me', badge: 'STAFF' },
    ]
  },
  {
    id: 'panduan',
    label: 'Panduan',
    icon: BookOpen,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    roles: ['director', 'finance', 'hr'], // All admin roles can see this
    items: [
      { id: 'panduan', label: 'Panduan Sistem', icon: BookOpen, path: '/panduan', badge: 'BACA' },
    ]
  },
  {
    id: 'home',
    label: 'Home',
    icon: Home,
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    roles: ['director', 'finance'],
    standalone: true,
    path: '/',
  },
  {
    id: 'ai-insights',
    label: 'AI Insights',
    icon: Brain,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    roles: ['director', 'finance'],
    standalone: true,
    path: '/ai-analysis',
    badge: 'AI',
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: DollarSign,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    roles: ['director', 'finance'], // Director & Finance only
    items: [
      { id: 'revenue', label: 'Revenue', icon: TrendingUp, path: '/revenue' },
      { id: 'expenses', label: 'Expenses', icon: CreditCard, path: '/expenses' },
      { id: 'expense-claims', label: 'Tuntutan Belanja', icon: Receipt, path: '/expense-claims', badgeKey: 'pendingClaims', roles: ['director', 'finance', 'hr'] },
      { id: 'cashflow', label: 'Cashflow', icon: Wallet, path: '/cashflow' },
      { id: 'hutang', label: 'Hutang', icon: FileBarChart, path: '/hutang', badge: 'NEW' },
      { id: 'loans', label: 'Loan', icon: Landmark, path: '/loans' },
      { id: 'pnl', label: 'Profit & Loss', icon: Target, path: '/profit-loss' },
      { id: 'balance', label: 'Balance Sheet', icon: Building2, path: '/balance-sheet' },
      { id: 'summary', label: 'Monthly Summary', icon: FileText, path: '/monthly-summary' },
      { id: 'assets', label: 'Aset', icon: Package, path: '/assets' },
      { id: 'einvoice', label: 'e-Invoice LHDN', icon: FileSignature, path: '/einvoice', badge: 'NEW' },
    ]
  },
  {
    id: 'hr',
    label: 'HR & Staff',
    icon: Briefcase,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    roles: ['director', 'finance', 'hr'], // All admin roles can see this
    items: [
      { id: 'hr', label: 'HR Hub', icon: UserCog, path: '/hr' },
      { id: 'staff-payroll', label: 'Staff & Payroll', icon: Users, path: '/staff-payroll' },
      { id: 'org-chart', label: 'Carta Organisasi', icon: Building2, path: '/org-chart', badge: 'NEW' },
      { id: 'team-calendar', label: 'Kalendar Pasukan', icon: Calendar, path: '/team-calendar' },
      { id: 'announcements', label: 'Pengumuman', icon: Megaphone, path: '/announcements', badge: 'NEW' },
      { id: 'lateness', label: 'Lateness Report', icon: Clock, path: '/lateness', badge: 'NEW' },
      { id: 'salary-advances', label: 'Advance Gaji (EWA)', icon: Coins, path: '/salary-advances', badge: 'NEW' },
      { id: 'lhdn-forms', label: 'Borang LHDN/KWSP', icon: FileSignature, path: '/lhdn-forms' },
    ]
  },
  {
    id: 'strategic',
    label: 'Strategic Insights',
    icon: TrendingUp,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    roles: ['director', 'finance'], // Director & Finance only
    items: [
      { id: 'staff-analytics', label: 'Staff Analytics', icon: Activity, path: '/staff-analytics' },
      { id: 'hiring', label: 'Hiring Decision', icon: UserPlus, path: '/hiring' },
    ]
  },
  {
    id: 'security',
    label: 'Keselamatan',
    icon: Shield,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    roles: ['director'],
    items: [
      { id: 'audit-logs', label: 'Audit Log', icon: Shield, path: '/audit-logs' },
    ]
  },
];

const Sidebar = () => {
  const { sidebarOpen, setSidebarOpen, userRole, user } = useApp();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const [expandedCategories, setExpandedCategories] = useState(['finance', 'hr', 'strategic']);
  const [pendingClaims, setPendingClaims] = useState(0);

  // Filter navigation categories based on user role.
  // Categories whose `roles` don't match the user are hidden by default.
  // EXCEPT: a category may show if at least one item has its own `roles` array
  // that includes the user. In that case, only those visible items are kept.
  const NAV_CATEGORIES = useMemo(() => {
    if (!userRole) return [];
    // Read-only viewer (e.g. Manus AI auditor) sees everything Director sees.
    const effectiveRole = userRole === 'viewer' ? 'director' : userRole;
    return ALL_NAV_CATEGORIES.reduce((out, category) => {
      const categoryAllowed = category.roles.includes(effectiveRole);
      const visibleItems = (category.items || []).filter((item) => {
        if (item.roles && Array.isArray(item.roles)) return item.roles.includes(effectiveRole);
        return categoryAllowed;
      });
      if (visibleItems.length > 0) {
        out.push({ ...category, items: visibleItems });
      } else if (categoryAllowed && category.standalone) {
        out.push(category);
      }
      return out;
    }, []);
  }, [userRole]);

  // Fetch dynamic counts (pending expense claims) for admin roles
  useEffect(() => {
    if (!['director', 'finance', 'hr'].includes(userRole)) return;
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/expense-claims?status=pending`,
          { credentials: 'include' }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setPendingClaims(data?.summary?.pending || 0);
      } catch { /* silent */ }
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 60_000); // refresh every 1 min
    return () => { cancelled = true; clearInterval(t); };
  }, [userRole]);

  const dynamicBadges = useMemo(
    () => ({ pendingClaims }),
    [pendingClaims]
  );

  // Handle logout
  const handleLogout = async () => {
    try {
      // Attach Bearer token (Safari ITP fallback) so backend can revoke server-side session
      const headers = {};
      try {
        const token = localStorage.getItem('ua_session_token');
        if (token) headers.Authorization = `Bearer ${token}`;
      } catch { /* private mode */ }
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      try { localStorage.removeItem('ua_session_token'); } catch { /* ignore */ }
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close sidebar on mobile (when screen width < 1024px)
      if (window.innerWidth < 1024 && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false);
      }
    };
    if (sidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen, setSidebarOpen]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  return (
    <>
      {/* Mobile Overlay - only visible on mobile when sidebar is open */}
      <div className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 lg:hidden ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} />
      
      {/* Sidebar Container */}
      <div 
        ref={sidebarRef} 
        className={`
          fixed top-0 left-0 h-full w-64 bg-white z-50 shadow-2xl
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0 lg:shadow-lg
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-red-700 to-red-800">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-white">UA Syarikat</h2>
              <p className="text-red-200 text-xs">Kewangan & HR</p>
            </div>
            {/* Close button - only visible on mobile */}
            <button onClick={() => setSidebarOpen(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors lg:hidden">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
          
          {/* User Info */}
          {user && (
            <div className="mt-3 pt-3 border-t border-white/20">
              <div className="flex items-center gap-3">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full border-2 border-white/30" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <span className="text-white font-bold">{user.name?.[0] || 'U'}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm truncate">{user.name}</p>
                  <p className="text-red-200 text-xs truncate">{user.email}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-bold rounded-full ${
                    userRole === 'director' ? 'bg-amber-500 text-white' :
                    userRole === 'finance' ? 'bg-emerald-500 text-white' :
                    userRole === 'staff' ? 'bg-indigo-500 text-white' :
                    userRole === 'viewer' ? 'bg-slate-600 text-white' :
                    'bg-blue-500 text-white'
                  }`}>
                    {userRole === 'director' ? 'DIRECTOR' :
                     userRole === 'finance' ? 'FINANCE' :
                     userRole === 'staff' ? 'STAFF' :
                     userRole === 'viewer' ? 'VIEWER · READ-ONLY' : 'HR'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="p-3 overflow-y-auto h-[calc(100%-200px)]">
          <div className="space-y-2">
            {NAV_CATEGORIES.map((category) => {
              const CategoryIcon = category.icon;
              
              // Standalone item (no expandable group, renders as direct link)
              if (category.standalone) {
                const isActive = location.pathname === category.path;
                return (
                  <Link
                    key={category.id}
                    to={category.path}
                    onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive
                        ? 'bg-red-700 text-white shadow-md'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className={`p-1.5 rounded-lg ${isActive ? 'bg-white/20' : category.bgColor}`}>
                      <CategoryIcon className={`w-4 h-4 ${isActive ? 'text-white' : category.color}`} />
                    </div>
                    <span className="font-semibold text-sm">{category.label}</span>
                    {category.badge && (
                      <span className={`ml-auto px-2 py-0.5 text-xs font-bold rounded-full ${
                        category.badge === 'AI' ? 'bg-purple-500 text-white' :
                        category.badge === 'NEW' ? 'bg-emerald-500 text-white' :
                        'bg-gray-200 text-gray-600'
                      }`}>
                        {category.badge}
                      </span>
                    )}
                  </Link>
                );
              }
              
              const isExpanded = expandedCategories.includes(category.id);
              const hasActiveItem = category.items.some(item => location.pathname === item.path);
              
              return (
                <div key={category.id} className="rounded-xl overflow-hidden">
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 transition-all ${
                      hasActiveItem ? category.bgColor : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-1.5 rounded-lg ${category.bgColor}`}>
                        <CategoryIcon className={`w-4 h-4 ${category.color}`} />
                      </div>
                      <span className={`font-semibold text-sm ${hasActiveItem ? category.color : 'text-gray-700'}`}>
                        {category.label}
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>

                  {/* Category Items */}
                  <div className={`transition-all duration-200 ease-in-out overflow-hidden ${
                    isExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="pl-4 pr-2 pb-2 space-y-1">
                      {category.items.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname === item.path;
                        const dynamicCount = item.badgeKey ? dynamicBadges[item.badgeKey] : null;
                        const showCount = item.badgeKey && dynamicCount > 0;
                        return (
                          <Link
                            key={item.id}
                            to={item.path}
                            onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                              isActive
                                ? 'bg-red-700 text-white shadow-md'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span className="font-medium text-sm">{item.label}</span>
                            {showCount && (
                              <span
                                data-testid={`badge-${item.id}`}
                                className={`ml-auto px-2 py-0.5 text-xs font-bold rounded-full bg-amber-500 text-white animate-pulse`}
                                title={`${dynamicCount} pending`}
                              >
                                {dynamicCount}
                              </span>
                            )}
                            {!showCount && item.badge && (
                              <span className={`ml-auto px-2 py-0.5 text-xs font-bold rounded-full ${
                                item.badge === 'NEW' ? 'bg-emerald-500 text-white' :
                                item.badge === 'AI' ? 'bg-purple-500 text-white' :
                                item.badge === 'LIVE' ? 'bg-blue-500 text-white animate-pulse' :
                                'bg-gray-200 text-gray-600'
                              }`}>
                                {item.badge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </nav>

        {/* Footer with Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-gray-100 bg-gray-50">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors mb-2"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span className="font-medium text-sm">Log Keluar</span>
          </button>
          <p className="text-xs text-gray-400 text-center">v2.0 • AI-Powered Dashboard</p>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
